/**
 * AddOrgModal component for adding a new organization
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Object} props.newOrg - New organization data
 * @param {Function} props.setNewOrg - Function to update new organization data
 * @param {boolean} props.isLoading - Whether the form is submitting
 * @param {Function} props.onClose - Function to close the modal
 * @param {Function} props.onSubmit - Function to handle form submission
 * @returns {JSX.Element} Rendered AddOrgModal component
 */
const AddOrgModal = ({
  isOpen,
  newOrg,
  setNewOrg,
  isLoading,
  onClose,
  onSubmit,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-bg-60 rounded-lg shadow-lg p-6 w-full max-w-md">
        <h3 className="text-base font-semibold mb-4">Add New Organization</h3>
        <form className="space-y-4">
          <div>
            <label htmlFor="orgName" className="block text-xs font-medium text-fg-50 mb-1">
              Organization Name
            </label>
            <input
              type="text"
              id="orgName"
              className="w-full py-1.5 px-3 text-xs rounded-md bg-bg-50 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
              value={newOrg.name}
              onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
              placeholder="e.g., Acme Corp"
            />
          </div>
          <div>
            <label htmlFor="orgDomain" className="block text-xs font-medium text-fg-50 mb-1">
              Domain
            </label>
            <input
              type="text"
              id="orgDomain"
              className="w-full py-1.5 px-3 text-xs rounded-md bg-bg-50 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
              value={newOrg.domain}
              onChange={(e) => setNewOrg({ ...newOrg, domain: e.target.value })}
              placeholder="example.com"
            />
          </div>
          {/* Preview */}
          <div className="bg-bg-50 rounded-lg p-4 mt-2 w-full">
            <h4 className="text-xs font-medium text-fg-50 mb-2 font-mono">Organization Preview</h4>
            <div className="space-y-2 text-xs text-fg-60">
              <div className="flex justify-between">
                <span className="font-mono">Name:</span>
                <span className="text-fg-50 font-mono">{newOrg.name || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-mono">Domain:</span>
                <span className="text-fg-50 font-mono">{newOrg.domain || '—'}</span>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              className="px-3 py-1.5 text-xs bg-bg-50 text-fg-50 rounded-md hover:bg-bg-50 transition-colors"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-3 py-1.5 text-xs bg-ac-02 hover:bg-ac-01 rounded-md transition-colors shadow-sm text-white dark:text-black  flex items-center"
              onClick={onSubmit}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  <span>Processing...</span>
                </>
              ) : (
                <span>Add Organization</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddOrgModal;
